import { FC, useCallback, useEffect, useRef, useState } from "react";
import useGridFilter, { CreateSelectorGridFilter } from "../../../system/hooks/useGridFilter";
import { useAppContext } from "../../../system/providers/appContextProvider";
import { usePluginContext } from "../../../system/providers/plugin";
import { IGoodsClassified2ViewDTO } from "../../../libs/coreapi-dto/dirs/goods";
import { GoodsDataProvider } from "../../../Services/DataProviders/GoodsDataProvider";
import BaseCommandsPanel from "../../../components/commandsPanels/BaseCommandsPanel";
import Tooltip from "../../../components/tooltips/Tooltip";
import { FilePlusButton } from "../../../components/buttons/iconButtons/other/FileAndFolderCollection";
import { Delete2Button } from "../../../components/buttons/iconButtons/action/MainCollection";
import styles from "./styles/GoodsClassifierItemsPluginView.module.scss"
import GoodsSelectorModal from "../Goods/GoodsSelectorModal";
import { RotationRightButton } from "../../../components/buttons/iconButtons/action/RotationCollection";
import { MessageModalWindow } from "../../../components/modalWindows/MessageModalWindow";
import { Spinner } from "../../../components/spiner/Spinner";
import { useTranslation } from "react-i18next";
import { ChangeableGrid, IChangeDataGrid } from "../../../components/grids/changeableGrid/ChangeableGrid";
import { DocumentType } from "../../../@types/enumsGlobal";
import { useTimeout } from "../../../system/hooks/useTimeout";
import { IEntitySimpleDTO } from "../../../libs/coreapi-dto/@types/common";

export interface ICopyGridProps extends ISelectorGridProps {
    gridId: string,
    plugin: IPluginSettings,
    baseGridFilter?: IGridFilter,
    id?: string,
}

interface IMoveGoodsInfo {
    goodsId: string,
    goodsName: string,
    currentGroupName: string
    currentGroupId: string
}

export const GoodsClassifierItemsPluginView: FC<ICopyGridProps> = (props) => {
    const appContext = useAppContext();
    const pluginContext = usePluginContext();
    const goodsDataProvider = new GoodsDataProvider(appContext.coreApiService);

    const { t } = useTranslation();
    const baseT = (value: string) => t('directory.goodsClassifier.' + value);

    const [gridFilter, dispatchGridFilter] = useGridFilter(CreateSelectorGridFilter);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [data, setData] = useState<IGoodsClassified2ViewDTO[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [displayItems, setDisplayItems] = useState<IGoodsClassified2ViewDTO[]>([]);
    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    
    const [showModalAddGoods, setShowModalAddGoods] = useState<boolean>(false);
    const [showModalDeleteGoods, setShowModalDeleteGoods] = useState<boolean>(false);
    const [showModalMoveGoods, setShowModalMoveGoods] = useState<boolean>(false);
    const [moveGoodsInfo, setMoveGoodsInfo] = useState<IMoveGoodsInfo | null>(null);
    
    const documentType = DocumentType.goodsClassifier;
    const isDeleted = pluginContext.masterGrid.selectedItem?.isDeleted;
    const idGoodsClassifierGlobal = pluginContext.masterGrid.selectedItem?.idGlobal as string;
    const [currentGroupName, setCurrentGroupName] = useState<string>('');
   
    const [setTimer, clearTimer] = useTimeout();
    const selectedItemRef = useRef<IGridRow>();
    const currentIdRef = useRef<string>('');
    const abortRef = useRef<AbortController | null>(null);
    const pendingGoodsToAdd = useRef<{ idGlobal: string, entity: IEntitySimpleDTO } | null>(null);
    
    useEffect(() => {
        dispatchGridFilter({
            type: "changeNumberPerPage",
            payload: { numberPerPage: 15 }
        });
        dispatchGridFilter({
            type: "changePageNumber",
            payload: { pageNumber: 1 }
        });
    }, []);

    useEffect(() => {
        const selectedItem = pluginContext.masterGrid.selectedItem;
        if (selectedItem?.idGlobal) {
            currentIdRef.current = selectedItem.idGlobal;
            setCurrentGroupName(selectedItem.displayName || '');
        }
    }, [pluginContext.masterGrid.selectedItem]);
    
    useEffect(() => {
        if (currentIdRef.current) {
            getViewClassified(currentIdRef.current);
        }
    }, [gridFilter.pageNumber, gridFilter.numberPerPage, gridFilter.fieldOrderBy, gridFilter.orderDesc]);

    useEffect(() => {
        if (idGoodsClassifierGlobal) {
            currentIdRef.current = idGoodsClassifierGlobal;
            getViewClassified(idGoodsClassifierGlobal);
            setSelectedItem(undefined);
        }
    }, [idGoodsClassifierGlobal]);

    const getViewClassified = async(idGoodsClassifierGlobal: string) => {
        abortRef.current?.abort();

        const controller = new AbortController();
        abortRef.current = controller;

        setIsLoading(true);
        
        try {
            goodsDataProvider.getViewClassified(
                {
                    ...gridFilter,
                    paramFilter: { idGoodsClassifierGlobal: idGoodsClassifierGlobal },
                    columnFilters: [
                        ...(gridFilter.columnFilters || []),
                        { name: "deleted", operator: "Eq", value: false }
                    ]
                },
                (data, totalCount) => {
                    setData(data);
                    setTotalCount(totalCount);
                    setDisplayItems(data);

                    setIsLoading(false);
                },
                controller.signal
            ); 

        } catch (error) {
            console.error(error);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        return () => {
            abortRef.current?.abort();
        };
    }, []);

    const handleRatingChange = async (changeData: IChangeDataGrid) => {
        if (changeData.propertyName !== "rating") return;

        let newRating = changeData.value as number;
        const itemId = changeData.idGlobal;

        if (newRating > 999) {
            newRating = 999;
            console.warn(baseT('ratingLimitTo'));
        } else if (newRating < 0) {
            newRating = 0;
            console.warn(baseT('ratingNegative'));
        }
        setIsLoading(true);
        try {
            goodsDataProvider.setRating(
                itemId,
                currentIdRef.current,
                newRating,
                null,
                () => {
                    const updatedData = data.map(item =>
                        item.idGlobal === itemId
                            ? { ...item, rating: newRating }
                            : item
                    );
                    const updatedDisplayItems = displayItems.map(item =>
                        item.idGlobal === itemId
                            ? { ...item, rating: newRating }
                            : item
                    );
                    setData(updatedData);
                    setDisplayItems(updatedDisplayItems);
                    setIsLoading(false);
                }
            );
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const updatedData = (changeData: IChangeDataGrid) => {
        if (changeData.propertyName === "rating") {
            handleRatingChange(changeData);
            return;
        };
        setDisplayItems(prevItems =>
            prevItems.map(item =>
                item.idGlobal === changeData.idGlobal
                    ? { ...item, [changeData.propertyName]: changeData.value }
                    : item
            )
        );
    };

    const handleColumnEnter = (changeData: IChangeDataGrid) => {
        if (changeData.propertyName === "rating") {
            handleRatingChange(changeData);
        };
    };

    const handleDelete = async () => {
        if (!selectedItem?.idGlobal) return;
        setIsLoading(true);
        try {
            goodsDataProvider.detachFromClassifier(selectedItem?.idGlobal, currentIdRef.current, () => {
                getViewClassified(currentIdRef.current);
                setShowModalDeleteGoods(false);
            });
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAdd = async (entity: IEntitySimpleDTO) => {
        if (!entity?.idGlobal) return;

        setIsLoading(true);
        try {
            await goodsDataProvider.attachToClassifier(entity.idGlobal, currentIdRef.current);
            getViewClassified(currentIdRef.current);
            setShowModalAddGoods(false);
        } catch (error: any) {
            const detail = error.response?.data?.detail ?? error.message ?? "";

            if (detail.includes("уже содержится в группе аналогов")) {
                const match = detail.match(/\(([^,]+),\s*([a-f0-9-]+)\)/);
                const [currentGroupName, currentGroupId] = match
                    ? [match[1].trim(), match[2].trim()]
                    : ["", ""];

                setMoveGoodsInfo({
                    goodsId: entity.idGlobal,
                    goodsName: entity.displayName || '',
                    currentGroupName: currentGroupName,
                    currentGroupId: currentGroupId
                });
                setShowModalMoveGoods(true);
                pendingGoodsToAdd.current = { idGlobal: entity.idGlobal, entity };
            } else {
                setShowModalAddGoods(false);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleMoveGoods = async () => {
        if (!moveGoodsInfo || !pendingGoodsToAdd.current) return;

        setIsLoading(true);
        try {
            goodsDataProvider.moveFromClassifier(
                moveGoodsInfo.goodsId,
                moveGoodsInfo.currentGroupId,
                currentIdRef.current,
                () => {
                    goodsDataProvider.setRating(
                        moveGoodsInfo.goodsId,
                        currentIdRef.current,
                        0,
                        null,
                        () => {
                            getViewClassified(currentIdRef.current);
                            setShowModalMoveGoods(false);
                            setShowModalAddGoods(false);
                            setMoveGoodsInfo(null);
                            pendingGoodsToAdd.current = null;
                        }
                    )
                }
            );
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleCancelMove = () => {
        setShowModalMoveGoods(false);
        setMoveGoodsInfo(null);
        pendingGoodsToAdd.current = null;
    };

    const handleRefresh = () => {
        if (currentIdRef.current) {
            getViewClassified(currentIdRef.current);
        };
    };

    const getCurrentGroupName = useCallback((): string => {
        return currentGroupName || baseT('unknownGroup');
    }, [currentGroupName, baseT]);

    const getGoodsNameById = useCallback((idGlobal: string | undefined): string => {
        if (!idGlobal) return '';
        return data.find(item => item.idGlobal === idGlobal)?.goodsName || '';
    }, [data]);

    const getDeleteMessage = useCallback(() => 
        baseT('confirmDeleteFromGroup').replace('{{goodsName}}', getGoodsNameById(selectedItem?.idGlobal)),
        [baseT, getGoodsNameById, selectedItem]
    );

    const getMoveMessage = useCallback(() => {
        if (!moveGoodsInfo) return '';
        
        return baseT('confirmMoveMessage')
            .replace('{{firstPart}}', baseT('goodsAlreadyInGroup')
                .replace('{{goodsName}}', moveGoodsInfo.goodsName)
                .replace('{{currentGroupName}}', moveGoodsInfo.currentGroupName))
            .replace('{{secondPart}}', baseT('moveGoodsToGroup')
                .replace('{{targetGroupName}}', getCurrentGroupName()));
    }, [baseT, moveGoodsInfo, getCurrentGroupName]);
   
    return (
        <div>
            <div className={styles.commandsPanel}>
                <BaseCommandsPanel
                    groups={[
                        [
                            {
                                iconButton: <Tooltip title={t("documentsCommandsPanel.addNote")}>
                                    <FilePlusButton
                                        sizeVariant="mini"
                                        onClick={() => setShowModalAddGoods(true)}
                                        disabled={isDeleted}
                                    />
                                </Tooltip>
                            },
                            {
                                iconButton: <Tooltip title={t("documentsCommandsPanel.deleteNote")}>
                                    <Delete2Button
                                        sizeVariant="mini"
                                        colorVariant='danger'
                                        disabled={!selectedItem}
                                        onClick={() => { setShowModalDeleteGoods(true) }}
                                    />
                                </Tooltip>
                            },
                            {
                                iconButton: <Tooltip title={t("documentsCommandsPanel.refresh")}>
                                    <RotationRightButton
                                        sizeVariant="mini"
                                        onClick={handleRefresh}
                                        disabled={!currentIdRef.current}
                                    />
                                </Tooltip>
                            }
                        ]
                    ]}
                />
            </div>
            {isLoading ? <Spinner /> :
                <ChangeableGrid
                    gridId={props.gridId}
                    items={data}
                    data={data}
                    documentType={documentType}
                    searching
                    filter={gridFilter}
                    totalCount={totalCount}
                    plugin={props.plugin}
                    selectedItem={selectedItem}
                    hiddenPagination={{ hiddenNumberPage: true, hiddenCountRow: true }}
                    onColumnEnter={(changeData: IChangeDataGrid) => clearTimer(() => handleColumnEnter(changeData))}
                    onChangeData={(changeData: IChangeDataGrid) => setTimer(() => updatedData(changeData))}
                    onSelect={(row) => {
                        setSelectedItem(row)
                        selectedItemRef.current = row
                    }}
                    onDoubleClick={(row) => props.onDoubleClick?.(row)}
                    onSort={(i) => dispatchGridFilter({ type: "sort", payload: i.propertyName })}
                    onFilterDelete={(i) => dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })}
                    onPageNumberChange={(n) => dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                    onNumberPerPageChange={(n) => dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                    dataProvider={undefined}
                    sendRequest={isLoading}
                />
            }
            {
                showModalAddGoods &&
                <GoodsSelectorModal
                    showCheckBoxDeleted={true}
                    gridFilter={gridFilter}
                    ok={handleAdd}
                    cancel={() => setShowModalAddGoods(false)}
                />
            }
            {
                showModalDeleteGoods &&
                <MessageModalWindow
                    ok={{ onClick: handleDelete, disabled: isLoading }}
                    cancel={{ onClick: () => setShowModalDeleteGoods(false), disabled: isLoading }}
                    message={getDeleteMessage()}
                />
            }
            {
                showModalMoveGoods && moveGoodsInfo &&
                <MessageModalWindow
                    ok={{ onClick: handleMoveGoods, disabled: isLoading }}
                    cancel={{ onClick: handleCancelMove, disabled: isLoading }}
                    message={getMoveMessage()}
                />
            }
        </div>
    )
}